import { useState, useEffect, useReducer } from 'react';
import styled from 'styled-components';
import Pagination from 'components/ui-components/Pagination';
import Button from 'components/ui-components/Button';
import { useHTTP } from 'hooks/useRequest';
import ApprovalsUnassignedUsers from '../groups_users/approvals_unassigned_users';
import ApprovalsGroupsTabs from './approvals_groups_tabs';
import ApprovalsFilterGroups from './approvals_filter_groups';
import ApprovalsGroupsTable from './approvals_groups_table';
import ApprovalsGroupMessageModal from '../group_form/approvals_group_message_modal';
import {
  initialRequestState,
  requestActions,
  requestReducer,
  initialMessageModalState,
  messageModalReducer,
  messageModalActions,
  messageModalTypes,
  initialTableState,
  tableReducer,
  tableActions,
} from './approvals_groups_reducer';

const ApprovalGroupsListing = ({ usersUrl, groupsUrl }) => {
  const [showSkeletonScreen, setShowSkeletonScreen] = useState(true);
  const [showSkeletonTable, setShowSkeletonTable] = useState(false);

  const [addGroup, setAddGroup] = useState({ url: '', text: '' });
  const [unassignedUsers, setUnassignedUsers] = useState({});

  // Adding groupsUrl here prevents the need for useEffect to update the request url on load
  const [requestState, requestDispatch] = useReducer(requestReducer, {
    ...initialRequestState,
    url: groupsUrl,
  });

  const [tableState, tableDispatch] = useReducer(
    tableReducer,
    initialTableState,
  );

  const [messageModalState, messageModalDispatch] = useReducer(
    messageModalReducer,
    initialMessageModalState,
  );

  /* REQUEST */

  const buildRequestUrl = (baseUrl, filter, sort, page) => {
    const statusType = tableState.statusList.find(
      status => status.label.toLowerCase() === filter.status.toLowerCase(),
    );
    let statusParam;
    switch (statusType.value) {
      case 'active':
        statusParam = '1';
        break;
      case 'archived':
        statusParam = '0';
        break;
      default:
        statusParam = 'all';
    }
    const filterByStatus = `q[status_eq]=${statusParam}`;
    // Check for empty input value to prevent adding param to url
    const filterByName =
      filter.name.length > 0 ? `&q[name_cont]=${filter.name}` : '';
    const sortByParams = sort.type ? `&q[s]=${sort.type} ${sort.order}` : '';
    return `${baseUrl}?${filterByStatus}${filterByName}${sortByParams}&page=${page}`;
  };

  /* TABLE - FILTER */

  const filterGroupsHandler = (name, status) => {
    const filter = { name, status };

    tableDispatch({
      type: tableActions.FILTER_GROUPS,
      payload: filter,
    });

    // Page set to 1 to prevent trying to request a filtered page that doesn't exist
    const requestUrl = buildRequestUrl(groupsUrl, filter, tableState.sort, 1);

    // Enable request
    requestDispatch({
      type: requestActions.ENABLE_REQUEST,
      payload: { url: requestUrl, method: 'GET' },
    });

    setShowSkeletonTable(true);
  };

  /* TABLE - SORTING */

  const tableHeaders = {
    name: 'Group Name',
    description: 'Description',
    status: 'Status',
    date: 'Date Created',
    archive: 'Archive Approval Group',
    delete: 'Delete Approval Group',
  };

  const sortTypes = {
    name: 'name',
    description: 'description',
    status: 'status',
    date: 'created_at',
  };

  const createSortConfig = header => {
    const sort = { type: '', order: 'asc' };

    // When a new sort type is selected, set the type
    switch (header) {
      case tableHeaders.name:
        sort.type = sortTypes.name;
        break;
      case tableHeaders.description:
        sort.type = sortTypes.description;
        break;
      case tableHeaders.status:
        sort.type = sortTypes.status;
        break;
      case tableHeaders.date:
        sort.type = sortTypes.date;
        break;
      default:
    }

    // When a new sort type is selected, set the order to asc
    if (sort.type !== tableState.sort.type) {
      sort.order = 'asc';
      return sort;
    }

    // When the same sort type is selected, toggle the order
    if (tableState.sort.order === 'asc') {
      sort.order = 'desc';
    }

    return sort;
  };

  const sortGroupsHandler = header => {
    const sort = createSortConfig(header);

    const requestUrl = buildRequestUrl(groupsUrl, tableState.filter, sort, 1);

    requestDispatch({
      type: requestActions.ENABLE_REQUEST,
      payload: { url: requestUrl, method: 'GET' },
    });

    tableDispatch({
      type: tableActions.SORT_GROUPS,
      payload: sort,
    });

    setShowSkeletonTable(true);
  };

  /* PAGINATION */

  const groupsMessage = () => {
    if (tableState.totalPageCount === 1) {
      return `all ${tableState.groupCount}`;
    }
    const min = (tableState.page - 1) * tableState.perPage + 1;
    const max = Math.min(
      min + tableState.groups.length - 1,
      tableState.groupCount,
    );
    return `${min} - ${max} of ${tableState.groupCount}`;
  };

  const pageChangeHandler = pageNumber => {
    const requestUrl = buildRequestUrl(
      groupsUrl,
      tableState.filter,
      tableState.sort,
      pageNumber,
    );

    tableDispatch({
      type: tableActions.PAGE_CHANGE,
      payload: pageNumber,
    });

    requestDispatch({
      type: requestActions.ENABLE_REQUEST,
      payload: { url: requestUrl, method: 'GET' },
    });

    setShowSkeletonTable(true);
  };

  /* MESSAGE MODAL */

  const openModalHandler = (group, type) => {
    let modalType = '';

    switch (type) {
      case messageModalTypes.ARCHIVE:
        if (group.status === 'Active') {
          modalType = messageModalTypes.ARCHIVE;
        }
        break;
      case messageModalTypes.UNARCHIVE:
        if (group.status === 'Archived') {
          modalType = messageModalTypes.UNARCHIVE;
        }
        break;
      case messageModalTypes.DELETE:
        if (group.status === 'Active' && !group.can_delete) {
          modalType = messageModalTypes.DELETE_ACTIVE_GROUP;
        } else if (group.status === 'Archived' && !group.can_delete) {
          modalType = messageModalTypes.DELETE_ARCHIVE_GROUP;
        } else {
          modalType = messageModalTypes.DELETE;
        }
        break;
      default:
        return;
    }

    messageModalDispatch({
      type: `OPEN_${modalType}_MESSAGE_MODAL`,
      payload: { group },
    });
  };

  const modalActionHandler = () => {
    let requestUrl = '';
    let method = '';
    let body = {};
    switch (messageModalState.type) {
      case messageModalTypes.DELETE:
        requestUrl = messageModalState.group.delete_approval_group_url;
        method = 'DELETE';
        break;
      case messageModalTypes.DELETE_ACTIVE_GROUP:
      case messageModalTypes.ARCHIVE:
        requestUrl = messageModalState.group.change_approval_group_status_url;
        method = 'PUT';
        body = {
          approval_group: {
            status: 'archived',
          },
        };
        break;
      case messageModalTypes.UNARCHIVE:
        requestUrl = messageModalState.group.change_approval_group_status_url;
        method = 'PUT';
        body = {
          approval_group: {
            status: 'active',
          },
        };
        break;
      default:
        break;
    }

    requestDispatch({
      type: requestActions.ENABLE_REQUEST,
      payload: { url: requestUrl, method, body },
    });

    messageModalDispatch({ type: messageModalActions.CLOSE_MESSAGE_MODAL });
  };

  const closeMessageModal = () => {
    messageModalDispatch({ type: messageModalActions.CLOSE_MESSAGE_MODAL });
  };

  /* ADD GROUP REDIRECT */
  // The following redirects the user to the add group page
  // Url is supplied by the endpoint
  const addGroupHandler = () => {
    window.location.assign(addGroup.url);
  };

  /* ENDPOINT REQUEST */

  const { url, method, body, enabled } = requestState;

  const requestConfig = {
    method,
    url: enabled ? url : null,
    data: JSON.stringify(body) || {},
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  };

  const { data: httpData, error: httpError } = useHTTP(requestConfig);

  useEffect(() => {
    if (!httpData) return;
    setShowSkeletonScreen(false);
    setShowSkeletonTable(false);
    if (
      httpData.success &&
      (requestState.method === 'DELETE' || requestState.method === 'PUT')
    ) {
      // Send a request to get the updated list of groups after a DELETE or PUT request
      const requestUrl = buildRequestUrl(
        groupsUrl,
        tableState.filter,
        tableState.sort,
        tableState.page,
      );
      requestDispatch({
        type: requestActions.ENABLE_REQUEST,
        payload: { url: requestUrl, method: 'GET' },
      });
      return;
    }

    setAddGroup(httpData.routes.new_approval_group);
    setUnassignedUsers(httpData.unassigned_users);
    tableDispatch({
      type: tableActions.SET_GROUPS,
      payload: {
        groups: httpData.approval_groups,
        groupCount: httpData.meta_data.total,
        page: httpData.meta_data.page,
        perPage: httpData.meta_data.per_page,
        totalPageCount: httpData.meta_data.total_pages,
        totalGroupCount: httpData.meta_data.total_approval_groups,
        statusList: httpData.filters,
      },
    });
    requestDispatch({ type: requestActions.DISABLE_REQUEST });
  }, [httpData]);

  useEffect(() => {
    if (!httpError) return;
    setShowSkeletonScreen(false);
    setShowSkeletonTable(false);
    requestDispatch({ type: requestActions.DISABLE_REQUEST });
  }, [httpError]);

  if (showSkeletonScreen) {
    const skeletonArray = Array.from(Array(30).keys());
    return (
      <div className="width-100">
        <div className="skeleton skeleton__unassigned-users width-100 mt-16 mb-55" />
        <div className="skeleton skeleton__tabs mb-25" />
        <div>
          <div className="skeleton skeleton__filter mb-16" />
        </div>
        <div>
          {skeletonArray.map(key => (
            <div className="skeleton skeleton__table" key={key} />
          ))}
        </div>
      </div>
    );
  }

  const DisplayPagination = () =>
    tableState.totalPageCount > 1 && (
      <Pagination
        currentPage={tableState.page}
        totalCount={tableState.groupCount}
        pageSize={tableState.perPage}
        onPageChange={pageChangeHandler}
        className="mb-30"
      />
    );

  return (
    <>
      <ApprovalsUnassignedUsers
        unassignedUsers={unassignedUsers}
        endpointUrl={groupsUrl}
        className="mb-55"
      />
      {tableState.groupCount > 0 && (
        <p
          className={`ta-r mt-24 ${
            tableState.totalPageCount > 1 ? 'mb-0' : 'mb-14'
          }`}>
          Displaying <strong>{groupsMessage()}</strong> groups
        </p>
      )}
      <DisplayPagination />
      <ApprovalsGroupsTabs usersUrl={usersUrl} groupsUrl={groupsUrl} />
      {tableState.totalGroupCount > 0 && (
        <ApprovalsFilterGroups
          filterGroupsHandler={filterGroupsHandler}
          statusList={tableState.statusList}
          currentSearchParam={tableState.filter.name}
        />
      )}
      {tableState.groupCount > 0 && (
        <ApprovalsGroupsTable
          headers={tableHeaders}
          data={tableState}
          openModalHandler={openModalHandler}
          sortGroupsHandler={sortGroupsHandler}
          sortTypes={sortTypes}
          skeletonScreen={showSkeletonTable}
        />
      )}
      {tableState.groupCount === 0 && (
        <p className="fs-16 tc-grey-70 mb-30">
          Sorry, we can’t find any results for your search. Please try again.
        </p>
      )}

      <DisplayPagination />
      {tableState.totalGroupCount === 0 && (
        <p className="fs-16 mb-15">No order approval groups have been added</p>
      )}
      {/* Button should redirect to create group page */}
      <ApprovalBtn className="approvals-button" handleClick={addGroupHandler}>
        <i className="icon-group mr-5" />
        {addGroup.text}
      </ApprovalBtn>
      <ApprovalsGroupMessageModal
        options={messageModalState}
        closeModal={closeMessageModal}
        actionHandler={modalActionHandler}
      />
    </>
  );
};

const ApprovalBtn = styled(Button)`
  width: 7.5rem;
  min-height: 1.875rem;
  border: solid 1px #e9e9e9;
  background: linear-gradient(to bottom, #fafafa 0%, #dbdbdb 100%);
`;

export default ApprovalGroupsListing;
